import {rejects} from 'assert';
import $ from 'jquery';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {getCoreObjectIds} from 'app-core/framework/CoreObjectIntrospectionUtils';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {Collaborator, Owner, OwnerInfo} from 'auth/PermissionConstants';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreSource} from 'constants/SourceConstants';
import {UpdateOwnableParams} from 'create-components/OwnableConstants';
import {
  OwnableSettingsModalProps,
  OwnableSettingsWithCollaborationsModal,
} from 'create-components/OwnableSettingsModal';
// @ts-expect-error
import FilesForDeleteModal from 'folder-items/FilesForDeleteModal';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {typeToName} from 'util/NameUtil';
import omitKey from 'util/omitKey';
import {InputChangeEvent} from 'util/Types';

import * as PoliceableActions from '../collaborator-components/PoliceableActions';
import CustomSourceSettings from './CustomSourceSettings';
import * as FileBrowserDAO from './FileBrowserDAO';
import * as SourceActions from './SourceActions';
import SourceDAO, {UpdateData, UpdateSourceResponse} from './SourceDAO';

interface SourceAttributes {
  description: string;
  name: string;
  owner: Owner;
  ownerInfo: OwnerInfo;
  rootFolder: {api_identifier: string};
  reviewLifecycleIds?: Array<string>;
}

export interface SourceSettingsModalProps {
  allowAuditorToggling: boolean;
  archiveFn?: (ids: Array<string>, purpose: string, options: ArchiveModal.ArchiveOptionsForModal) => void;
  deleteFn?: (id: string) => JQueryPromise<void>;
  isReadOnly: boolean;
  onDelete?: () => void;
  onUpdate?: () => void;
  showConfirmationOnSave?: boolean;
  confirmationMessage?: string;
  onRequestClose: () => void;
  showCustomSettings: boolean;
  sourceId: string;
  sourceType: ModelTypeConstants.PROJECT | ModelTypeConstants.REGISTRY;
  canManageCollaborators: boolean;
}

export default function SourceSettingsModal(props: SourceSettingsModalProps): JSX.Element | null {
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const currentUser = EntityStore.currentUser();
  const [isSourceLoading, setIsSourceLoading] = useState(true);
  const [sourceAttributes, setSourceAttributes] = useState<SourceAttributes | null>(null);
  const [archived, setArchived] = useState(false);
  const [locked, setLocked] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [alwaysShowAncestorFolderNames, setAlwaysShowAncestorFolderNames] = useState(true);
  const [initialSourceObject, setInitialSourceObject] = useState<Partial<NavStoreSource>>({});
  const [_, setInitialFetchedCollaborators] = useState<Array<Collaborator>>([]);

  useOnMount(() => {
    LoadingScreen.show();
    $.when<Array<Collaborator> | NavStoreSource>(
      SourceDAO.fetchCollaborators(props.sourceId),
      FileBrowserDAO.fetchSource(props.sourceId)
    )
      .always(() => {
        LoadingScreen.hide();
      })
      // @ts-expect-error type is incorrect for $.when
      .done(([fetchedCollaborators]: [Array<Collaborator>], [source]) => {
        setInitialFetchedCollaborators(fetchedCollaborators);
        setInitialSourceObject(source);
        setCollaborators(fetchedCollaborators);
        setSourceAttributes({
          description: source.description,
          name: source.name,
          owner: source.s_owner,
          ownerInfo: source.ownerInfo,
          rootFolder: source.rootFolder,
          reviewLifecycleIds: source.reviewLifecyclesIds,
        });
        setArchived(source.archived);
        setLocked(source.locked);
        setIsPublic(source.public);
        setAlwaysShowAncestorFolderNames(source.alwaysShowAncestorFolderNames);
        setIsSourceLoading(false);
      })
      .fail((error) => {
        props.onRequestClose();
        notifications.notifyError(error);
      });
  });

  const _getUpdatedSourceObject = (
    initialSource: Partial<UpdateData>,
    source: Partial<UpdateData>
  ): Partial<UpdateData> => {
    const updatedKeys = Object.keys(source).filter(
      (k: keyof UpdateData) => !isEqual(initialSource[k], source[k])
    );
    return updatedKeys.reduce((o, key: keyof UpdateData) => Object.assign(o, {[key]: source[key]}), {});
  };

  const _handleSaveSettings = ({
    name,
    owner,
    description,
    collaborators: allCollaborators,
    collaborations,
    ownerInfo,
    reviewLifecycleApiIds,
  }: UpdateOwnableParams): Promise<UpdateSourceResponse> => {
    // Whitelist certain settings on collaborators
    const {allowAuditorToggling} = props;
    if (!allowAuditorToggling) {
      allCollaborators = allCollaborators?.map(
        (collaborator) => omitKey(collaborator, 'auditor') as Collaborator
      );
    }

    const updatedSourceObject = _getUpdatedSourceObject(
      {
        alwaysShowAncestorFolderNames: BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS
          ? initialSourceObject.alwaysShowAncestorFolderNames
          : undefined,
        archived: initialSourceObject.archived,
        // We have no way to get the initial collaborations at this point. So instead we will check for updates downstream.
        collaborations: undefined,
        collaborators: undefined,
        locked: initialSourceObject.locked,
        description: initialSourceObject.description ?? undefined,
        name: initialSourceObject.name,
        owner: initialSourceObject.s_owner!.handle,
        ownerInfo: initialSourceObject.ownerInfo,
        public: initialSourceObject.public,
      },
      {
        alwaysShowAncestorFolderNames: BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS
          ? alwaysShowAncestorFolderNames
          : undefined,
        archived,
        collaborations:
          collaborations != null ? convertToCollaborationCreateOrUpdates(collaborations) : undefined,
        collaborators: undefined,
        description,
        locked,
        name,
        owner: owner.handle,
        ownerInfo,
        public: isPublic,
        reviewLifecycleApiIds,
      }
    );

    return SourceActions.update(props.sourceId, updatedSourceObject);
  };

  const _handleDeleteSource = (): void => {
    FilesForDeleteModal.open({
      fileIds: [sourceAttributes!.rootFolder.api_identifier],
      doDelete: _handleDeleteSourceConfirmation,
      deleteDescription: (
        <span>
          the {typeToName(props.sourceId)} <strong>{sourceAttributes!.name}</strong>
        </span>
      ),
    });
  };

  const _handleDeleteSourceConfirmation = (): void => {
    props.deleteFn!(props.sourceId)
      .done((response) => {
        notifications.success(
          `Successfully deleted ${typeToName(props.sourceId)} **${sourceAttributes!.name}**.`
        );
        props.onRequestClose();
        if (props.onDelete) {
          props.onDelete();
        }
      })
      .fail(notifications.notifyError);
  };

  const _handleArchiveSource = (): void => {
    ArchiveModal.open({
      items: [
        {
          id: props.sourceId,
          name: sourceAttributes!.name,
        },
      ],
      onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveModal.ArchiveOptionsForModal) => {
        props.archiveFn?.(itemIds, purpose, options);
      },
    });
    props.onRequestClose();
    props.onUpdate?.();
  };

  const _handleLeave = (partyId: string): Promise<void> =>
    PoliceableActions.removeCollaborator(getCoreObjectIds([props.sourceId])[0], partyId)
      .then((response) => {
        props.onRequestClose();
        props.onUpdate?.();
      })
      .catch((response) => {
        if (response.isCanceled) {
          return;
        }
        return rejects(response);
      });

  if (isSourceLoading) {
    return null;
  }

  const nonOwnerCollaborators = collaborators.filter(({owner}) => !owner);
  const ownableSource = {
    name: sourceAttributes!.name,
    description: sourceAttributes!.description,
    owner: sourceAttributes!.owner,
    ownerInfo: sourceAttributes!.ownerInfo,
    collaborators: nonOwnerCollaborators,
  };

  // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
  function CustomSettingsComponent(innerProps: {isPosting?: boolean}): JSX.Element | null {
    return props.showCustomSettings ? (
      <CustomSourceSettings
        archived={archived}
        disablePublic={currentUser.disable_public}
        isReadOnly={props.isReadOnly || Boolean(innerProps.isPosting)}
        locked={locked}
        alwaysShowAncestorFolderNames={alwaysShowAncestorFolderNames}
        onChangeArchived={(e: InputChangeEvent) => {
          setArchived(e.target.checked);
        }}
        onChangeLocked={(e: InputChangeEvent) => {
          setLocked(e.target.checked);
        }}
        onChangePublic={(e: InputChangeEvent) => {
          setIsPublic(e.target.checked);
        }}
        onChangeAlwaysShowAncestorFolderNames={(e: InputChangeEvent) => {
          setAlwaysShowAncestorFolderNames(!e.target.checked);
        }}
        public={isPublic}
      />
    ) : null;
  }

  const ownableSettingsProps: OwnableSettingsModalProps = {
    customSettingsComponent: CustomSettingsComponent,
    doArchive: props.archiveFn ? _handleArchiveSource : null,
    doDelete: props.deleteFn ? _handleDeleteSource : null,
    doSubmit: _handleSaveSettings,
    existingOwnable: ownableSource,
    isReadOnly: props.isReadOnly,
    navigateOnSuccess: false,
    onLeave: _handleLeave,
    onUpdate: props.onUpdate,
    onRequestClose: props.onRequestClose,
    ownableId: props.sourceId,
    ownableType: props.sourceType,
    showConfirmDelete: false,
    canManageCollaborators: props.canManageCollaborators,
    ...(BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES && props.allowAuditorToggling
      ? {
          supportReviewLifecycleSelection: true,
          allowAuditorToggling: true,
          initialReviewLifecycleIds: sourceAttributes?.reviewLifecycleIds,
          showWarningMessageOnReviewLifecycleEdit: true,
        }
      : {allowAuditorToggling: props.allowAuditorToggling}),
  };

  return <OwnableSettingsWithCollaborationsModal {...ownableSettingsProps} />;
}

SourceSettingsModal.defaultProps = {
  isReadOnly: false,
};
